import { IComponentOptions } from "angular";
import { IOrganization, IOrganizationTree } from "@gtmhub/configuration/workday/models";
import { localize } from "@gtmhub/localization";
import { WorkdayUserProvisioningService } from "@gtmhub/configuration/workday/workday-user-provisioning-service";

export class WizardOrganizationsFilterEntriesCtrl {
  organizations: IOrganization[];
  selectedOrganizationIds: string[];
  filteredOrganizations: IOrganizationTree[];
  searchOrganizations: string;
  selectAllChecked: boolean;
  index: number;
  singleSelect: boolean;
  onSelected: (arg: { organizations: string[] }) => void;

  limit = 0;
  private organizationsTree: IOrganizationTree[]; // a list of the top level organizations
  private organizationsList: IOrganizationTree[]; // a list of all organizations
  private limitStep = 0;

  private readonly sizes = {
    entityHeight: 36,
  };

  public static $inject = ["WorkdayUserProvisioningService"];
  constructor(private workdayUserProvisioningService: WorkdayUserProvisioningService) {}

  $onInit(): void {
    this.searchOrganizations = localize("search_organizations");
  }

  $onChanges(): void {
    if (!this.selectedOrganizationIds) {
      this.selectedOrganizationIds = [];
    }

    this.buildTree();
    this.setInitialRenderingLimits();
    this.selectAllChecked = this.selectedOrganizationIds[0] === "*";
    if (this.selectAllChecked) {
      this.selectAllUpdated(true);
    }
    this.filteredOrganizations = this.organizationsList.slice();
  }

  loadMoreEntities(): void {
    if (this.limit < this.filteredOrganizations.length) {
      this.limit += this.limitStep;
    }
  }

  toggle(event: Event, organization: IOrganizationTree): void {
    event?.stopPropagation();
    organization.expanded = !organization.expanded;
    if (organization.children) {
      organization.children.forEach((child) => (child.visible = !child.visible));
    }
    const descendantsCount = this.countDescendants(organization);
    if (organization.expanded) {
      this.showChildren(organization);
      this.limit -= descendantsCount;
    } else {
      this.hideChildren(organization);
      this.limit += descendantsCount;
    }
  }

  searchTermUpdated(searchTerm: string): void {
    this.filterOrganizations(searchTerm, this.organizationsTree);
    this.filteredOrganizations = this.organizationsList.filter((org) => org.visible);
  }

  selectAllUpdated(selectAllChecked: boolean): void {
    this.organizationsTree.forEach((organization) => {
      this.selectUnselectAllChildren(organization, selectAllChecked);
    });

    if (selectAllChecked) {
      this.updateSelectedOrganizationsAllCheckedFlag();
    }
  }

  private filterOrganizations(searchTerm: string, organizationsTree: IOrganizationTree[]): void {
    organizationsTree.forEach((organization) => {
      const shouldBeDisplayed = organization.name.toLowerCase().includes(searchTerm.toLowerCase());
      organization.visible = shouldBeDisplayed;
      organization.expanded = shouldBeDisplayed;

      if (organization.parent && shouldBeDisplayed) {
        this.showOrganizationParents(organization.parent);
      }

      if (organization.children) {
        this.filterOrganizations(searchTerm, organization.children);
      }
    });
  }

  private showOrganizationParents(parent: IOrganizationTree) {
    parent.visible = true;
    parent.expanded = true;
    if (parent.parent) {
      this.showOrganizationParents(parent.parent);
    }
  }

  private hideChildren(organization: IOrganizationTree): void {
    organization.children.forEach((child) => {
      child.visible = false;
      if (child.children) {
        this.hideChildren(child);
      }
    });
  }

  private showChildren(organization: IOrganizationTree): void {
    if (organization.expanded) {
      organization.children.forEach((child) => {
        child.visible = true;
        if (child.children) {
          this.showChildren(child);
        }
      });
    }
  }

  selectUnselectAllChildren(organization: IOrganizationTree, selected: boolean, event?: Event): void {
    event?.stopPropagation();
    if (!selected) {
      this.selectAllChecked = false;
    }
    organization.showSelectAllChildren = !selected;
    organization.selected = selected;
    organization.children?.forEach((organizationChild) => {
      organizationChild.selected = selected;
      this.updateSelectedOrganizations();
      this.selectUnselectAllChildren(organizationChild, selected);
    });
    this.assignShowSelectAllChildrenValueOnOrganizationParents(organization);
    this.updateSelectedOrganizations();
  }

  select(organization: IOrganizationTree): void {
    if (this.singleSelect) {
      if (organization.inSelection) {
        this.unselectAllValues();
        organization.selected = true;
        this.onSelected({
          organizations: [organization.id],
        });
      }
    } else {
      organization.selected = !organization.selected;
      // If organization is unselected we want to set select all to false
      if (!organization.selected) {
        this.selectAllChecked = false;
      }

      this.assignShowSelectAllChildrenValueOnOrganizationParents(organization);
      this.updateSelectedOrganizations();
    }
  }

  private unselectAllValues(): void {
    this.organizationsTree.forEach((organization) => {
      this.unselectAllChildren(organization);
    });
  }

  private unselectAllChildren(organization: IOrganizationTree) {
    organization.selected = false;
    if (organization.children) {
      organization.children.forEach((childOrganization) => {
        this.unselectAllChildren(childOrganization);
      });
    }
  }

  private updateSelectedOrganizations(): void {
    const selectedOrganizationIds = this.organizationsList.filter((organization) => organization.selected).map((organization) => organization.id);
    this.onSelected({
      organizations: selectedOrganizationIds,
    });
  }

  private updateSelectedOrganizationsAllCheckedFlag(): void {
    this.onSelected({
      organizations: ["*"],
    });
  }

  private setInitialRenderingLimits(): void {
    const { entityLimitStep } = this.calculateRenderingSteps();
    this.limit = entityLimitStep;
    this.limitStep = entityLimitStep;
  }

  private calculateRenderingSteps(): { entityLimitStep: number } {
    const windowHeight = document.documentElement.clientHeight;
    const enlargementCoefficient = 1.1;
    return {
      entityLimitStep: Math.ceil((windowHeight * enlargementCoefficient) / this.sizes.entityHeight),
    };
  }

  private countDescendants(organization: IOrganizationTree): number {
    return organization.children.length + organization.children.reduce((count, child) => count + this.countDescendants(child), 0);
  }

  private buildTree() {
    const enrichedOrganizations = this.organizations.map((organization) => {
      const color = this.workdayUserProvisioningService.assignOrganizationColorToName(organization.name);
      return {
        ...organization,
        children: [],
        selected: this.selectedOrganizationIds.includes(organization.id),
        expanded: true, // all  teams are expanded by default;
        visible: true, // all  teams are visible by default;
        parent: null,
        depth: 0,
        orgColor: color,
        initials: this.workdayUserProvisioningService.nameToInitials(organization.name),
      };
    }) as IOrganizationTree[];

    const enrichedOrganizationsIdMap = enrichedOrganizations.reduce((acc, el, i) => {
      acc[el.id] = i;
      return acc;
    }, {});

    const root = { children: [] } as IOrganizationTree;

    enrichedOrganizations.forEach((el) => {
      if (el.name) {
        if (!el.parentId) {
          root.children.push(el);
        } else {
          const parentEl = enrichedOrganizations[enrichedOrganizationsIdMap[el.parentId]];
          parentEl.children.push(el);
          el.parent = parentEl;
        }
      }
    });
    this.assignDepth(root.children);

    root.children.forEach((organization) => {
      this.assignShowSelectAllChildrenValue(organization);
    });

    this.organizationsTree = root.children;
    this.organizationsList = this.flattenTree(root);
  }

  private assignShowSelectAllChildrenValue(organization: IOrganizationTree) {
    organization.showSelectAllChildren = !this.isOrganizationAndChildrenSelected(organization);
    if (organization.children) {
      organization.children.forEach((childOrganization) => {
        this.assignShowSelectAllChildrenValue(childOrganization);
      });
    }
  }

  private assignShowSelectAllChildrenValueOnOrganizationParents(organization: IOrganizationTree): void {
    organization.showSelectAllChildren = !this.isOrganizationAndChildrenSelected(organization);
    if (organization.parent) {
      this.assignShowSelectAllChildrenValueOnOrganizationParents(organization.parent);
    }
  }

  private isOrganizationAndChildrenSelected(organization: IOrganizationTree): boolean {
    if (!organization.selected) {
      return false;
    } else if (organization.children) {
      for (let i = 0; i < organization.children.length; i++) {
        const childOrganization = organization.children[i];
        if (!this.isOrganizationAndChildrenSelected(childOrganization)) {
          return false;
        }
      }
    }
    return true;
  }

  private flattenTree(root: IOrganizationTree): IOrganizationTree[] {
    const iterator = (list: IOrganizationTree[], node: IOrganizationTree) => {
      node.children.sort((a, b) => {
        if (a.name > b.name) {
          return 1;
        } else if (a.name < b.name) {
          return -1;
        } else {
          return 0;
        }
      });
      list.push(node);
      node.children.forEach((child) => {
        iterator(list, child);
      });
    };
    const nodes: IOrganizationTree[] = [];
    iterator(nodes, root);
    nodes.shift(); // remove the root
    return nodes;
  }

  private assignDepth(arr, depth = 0, index = 0) {
    if (index < arr.length) {
      arr[index].depth = depth;
      if (arr[index].children.length) {
        this.assignDepth(arr[index].children, depth + 1, 0);
      }
      this.assignDepth(arr, depth, index + 1);
    }
  }
}

export const WizardOrganizationsFilterEntriesComponent: IComponentOptions = {
  bindings: {
    organizations: "<",
    selectedOrganizationIds: "<",
    singleSelect: "<",
    index: "<",
    onSelected: "&",
  },

  controller: WizardOrganizationsFilterEntriesCtrl,
  templateUrl: require("./organizations-filter-entries.jade"),
  controllerAs: "$ctrl",
};
